/**
 * Asset Batches — a procurement lot (one PO/invoice) that assets get registered under, via
 * Administration's 3rd tab alongside Configuration and Vendors. Sits ON TOP of the existing asset
 * register (`asset.contract.ts`'s `Asset`) rather than replacing it: `Asset.batchId` is a nullable
 * FK, so a batch never requires a different shape for the assets inside it.
 *
 * `assetCount`/`allocatedCount`/`pendingCount` and `allocationStatus` are NEVER stored — always
 * derived at read time (grouped off `Asset.batchId` / open `AssetAssignment` rows, see
 * ASSET_BATCH_ALLOCATION_STATUSES's doc comment in enums.ts), same reasoning as
 * `AssetType.assetCount` on the Configuration screen and `associatedAssetCount` on Vendor.
 *
 * Financial fields (`totalValue`/`currency`) are OMITTED (not nulled) from the response when the
 * caller lacks `asset.financial:read` — the same convention `asset.contract.ts`'s `assetSchema`
 * uses for `purchaseValue`/`purchaseCurrency`.
 *
 * CSV bulk import is a 3-step flow, not a single request: a raw (non-contract) multipart upload
 * stages parsed rows in Redis keyed by an opaque `uploadToken`; `importBatchAssetsPreview` validates
 * those staged rows against this org's real category/type catalog and returns counts + per-row
 * errors without writing anything; `importBatchAssetsCommit` re-validates and creates real `Asset`
 * rows (same fields `asset.contract.ts`'s `createAsset` writes) under this batch, transactionally.
 *
 * CSV columns: name, categoryCode, typeCode (required); serialNumber, purchaseDate, purchaseValue,
 * purchaseCurrency, condition, and — AST-24 — locationCode (an existing location's code),
 * warrantyExpiresOn (YYYY-MM-DD), warrantyMonths (derives the expiry from the purchase date; falls
 * back to the batch's own warrantyMonths), and assigneeCode (an employee code: the new asset is
 * assigned to that person, which additionally needs `asset:assign` over them).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import { assetCsvExportSchema } from './vendor.contract.js';
import {
  assetBatchAllocationStatusSchema,
  assetBatchStatusSchema,
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const assetBatchSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  batchNumber: z.string(),
  description: z.string().nullable(),
  vendorId: uuidSchema.nullable(),
  vendorName: z.string().nullable(),
  purchaseReference: z.string().nullable(),
  purchaseDate: dateOnlySchema.nullable(),
  categoryId: uuidSchema.nullable(),
  categoryName: z.string().nullable(),
  assetTypeId: uuidSchema.nullable(),
  assetTypeName: z.string().nullable(),
  warrantyMonths: z.number().int().nonnegative().nullable(),
  /** Stored lifecycle state — active | archived. */
  status: assetBatchStatusSchema,
  /** Derived, never stored — see this file's header comment. */
  allocationStatus: assetBatchAllocationStatusSchema,
  assetCount: z.number().int().nonnegative(),
  allocatedCount: z.number().int().nonnegative(),
  pendingCount: z.number().int().nonnegative(),
  /** With serial number recorded — a real data-quality signal (never a fabricated one). */
  serialisedCount: z.number().int().nonnegative(),
  /** Omitted (not null) unless the caller holds asset.financial:read. */
  totalValue: decimalStringSchema.optional(),
  currency: currencySchema.optional(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type AssetBatch = z.infer<typeof assetBatchSchema>;

/** Lean row for the "View Assets" drill-through under a batch — not the full Asset shape. */
export const assetBatchAssetRowSchema = z.object({
  id: uuidSchema,
  assetCode: z.string(),
  name: z.string(),
  serialNumber: z.string().nullable(),
  status: z.string(),
  condition: z.string(),
  isAllocated: z.boolean(),
  assignedToPersonId: uuidSchema.nullable(),
  assignedToName: z.string().nullable(),
});
export type AssetBatchAssetRow = z.infer<typeof assetBatchAssetRowSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listAssetBatchesQuerySchema = paginationQuerySchema.extend({
  cursor: uuidSchema.optional(),
  /** Matches batch number, description, or purchase reference. */
  q: z.string().trim().max(100).optional(),
  status: assetBatchStatusSchema.optional(),
  allocationStatus: assetBatchAllocationStatusSchema.optional(),
  vendorId: uuidSchema.optional(),
  categoryId: uuidSchema.optional(),
  /** Purchase-date range — the UI derives these from the org's real fiscal-quarter boundaries
   * (CLAUDE.md invariant 4: fiscal periods come from legal_entity.fiscal_year_start, never the
   * calendar), rather than the contract knowing anything about fiscal quarters itself. */
  purchaseDateFrom: dateOnlySchema.optional(),
  purchaseDateTo: dateOnlySchema.optional(),
});

export const createAssetBatchBodySchema = z.object({
  description: z.string().trim().max(500).optional(),
  vendorId: uuidSchema.optional(),
  categoryId: uuidSchema.optional(),
  assetTypeId: uuidSchema.optional(),
  purchaseReference: z.string().trim().max(100).optional(),
  purchaseDate: dateOnlySchema.optional(),
  totalValue: decimalStringSchema.optional(),
  currency: currencySchema.optional(),
  warrantyMonths: z.number().int().min(0).max(600).optional(),
});
export type CreateAssetBatchBody = z.infer<typeof createAssetBatchBodySchema>;

/** Every field optional; `batchNumber` is server-allocated and immutable. `status` here is how a
 * batch gets archived — no separate archive/reactivate route, matching Vendor's isActive-via-PATCH
 * convention. */
export const updateAssetBatchBodySchema = z.object({
  description: z.string().trim().max(500).nullable().optional(),
  vendorId: uuidSchema.nullable().optional(),
  categoryId: uuidSchema.nullable().optional(),
  assetTypeId: uuidSchema.nullable().optional(),
  purchaseReference: z.string().trim().max(100).nullable().optional(),
  purchaseDate: dateOnlySchema.nullable().optional(),
  totalValue: decimalStringSchema.nullable().optional(),
  currency: currencySchema.nullable().optional(),
  warrantyMonths: z.number().int().min(0).max(600).nullable().optional(),
  status: assetBatchStatusSchema.optional(),
});
export type UpdateAssetBatchBody = z.infer<typeof updateAssetBatchBodySchema>;

export const listAssetBatchAssetsQuerySchema = paginationQuerySchema.extend({
  cursor: uuidSchema.optional(),
  /** Unallocated = no open AssetAssignment. */
  allocation: z.enum(['all', 'allocated', 'unallocated']).default('all'),
});

// ---- Bulk import ----------------------------------------------------------------

/** Returned by the raw (non-contract) multipart upload route that stages a CSV's rows. */
export const assetImportUploadTokenSchema = z.string().min(8).max(200);

export const importAssetBatchBodySchema = z.object({
  uploadToken: assetImportUploadTokenSchema,
});

export const assetImportRowErrorSchema = z.object({
  row: z.number().int().positive(),
  field: z.string(),
  message: z.string(),
});
export type AssetImportRowError = z.infer<typeof assetImportRowErrorSchema>;

export const assetImportPreviewSchema = z.object({
  totalRows: z.number().int().nonnegative(),
  validRows: z.number().int().nonnegative(),
  errorRows: z.number().int().nonnegative(),
  errors: z.array(assetImportRowErrorSchema),
});
export type AssetImportPreview = z.infer<typeof assetImportPreviewSchema>;

export const assetImportResultSchema = z.object({
  created: z.number().int().nonnegative(),
  /** Rows that also carried an `assigneeCode` and were assigned to that person (AST-24). */
  assigned: z.number().int().nonnegative().default(0),
});
export type AssetImportResult = z.infer<typeof assetImportResultSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const assetBatchContract = c.router(
  {
    listBatches: {
      method: 'GET',
      path: '/',
      query: listAssetBatchesQuerySchema,
      responses: {
        200: paginatedSchema(assetBatchSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List asset batches',
      metadata: routeMeta('asset.batch:read'),
    },
    createBatch: {
      method: 'POST',
      path: '/',
      body: createAssetBatchBodySchema,
      responses: {
        201: assetBatchSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create an asset batch',
      metadata: routeMeta('asset.batch:manage'),
    },
    // Declared before getBatch: `/export` would otherwise match `/:batchId` as an id.
    exportBatches: {
      method: 'GET',
      path: '/export',
      query: listAssetBatchesQuerySchema.omit({ cursor: true, limit: true }),
      responses: {
        200: assetCsvExportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Export asset batches as CSV (asset:export)',
      metadata: routeMeta(['asset:export', 'asset.batch:read']),
    },
    getBatch: {
      method: 'GET',
      path: '/:batchId',
      responses: {
        200: assetBatchSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get an asset batch by id',
      metadata: routeMeta('asset.batch:read'),
    },
    updateBatch: {
      method: 'PATCH',
      path: '/:batchId',
      body: updateAssetBatchBodySchema,
      responses: {
        200: assetBatchSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update or archive an asset batch',
      metadata: routeMeta('asset.batch:manage'),
    },
    listBatchAssets: {
      method: 'GET',
      path: '/:batchId/assets',
      query: listAssetBatchAssetsQuerySchema,
      responses: {
        200: paginatedSchema(assetBatchAssetRowSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'List the assets registered under a batch',
      metadata: routeMeta('asset.batch:read'),
    },
    importBatchAssetsPreview: {
      method: 'POST',
      path: '/:batchId/import/preview',
      body: importAssetBatchBodySchema,
      responses: {
        200: assetImportPreviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Validate a staged asset-import CSV without creating anything',
      metadata: routeMeta(['asset.batch:manage', 'asset:create']),
    },
    importBatchAssetsCommit: {
      method: 'POST',
      path: '/:batchId/import/commit',
      body: importAssetBatchBodySchema,
      responses: {
        200: assetImportResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create real Asset rows from a validated staged CSV, under this batch',
      metadata: routeMeta(['asset.batch:manage', 'asset:create']),
    },
  },
  { pathPrefix: '/asset-management/batches' },
);
